import { KeyboardEvent, useEffect } from "react";

/**
 * Helper di accessibilità condivisi.
 *
 * Il design system Maggioli espone diversi componenti "cliccabili" che vengono
 * però renderizzati come elementi non interattivi (`div`, `i`, `span`).
 * Questi helper permettono di attribuire loro ruolo, focalizzabilità e
 * operabilità da tastiera senza modificare il design system.
 */

// Attiva l'handler su Invio e Barra spaziatrice, come farebbe un <button> nativo
export const onActivationKey =
    (action: Function) =>
    (e: KeyboardEvent<HTMLElement>): void => {
        if (e.key !== "Enter" && e.key !== " " && e.key !== "Spacebar") {
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        action();
    };

/**
 * Restituisce le proprietà necessarie a trasformare un elemento non interattivo
 * in un pulsante operabile da mouse, tastiera e tecnologie assistive.
 */
export const asButton = (action: Function, label?: string, extra: object = {}) => ({
    role: "button",
    tabIndex: 0,
    onClick: () => action(),
    onKeyDown: onActivationKey(action),
    ...(label !== undefined ? { "aria-label": label } : {}),
    ...extra,
});

// Classe per testo leggibile dai soli screen reader
export const SR_ONLY: string = "sr-only";

// Classe base dei pulsanti costituiti dalla sola icona
export const ICON_BUTTON: string = "bg-transparent border-0 p-0 cursor-pointer inline-flex items-center";

/**
 * Completa un controllo renderizzato dal design system che non è raggiungibile
 * tramite props: l'icona del menu generata internamente da `HeaderMenu`, oppure
 * un `Button` di sole icone, che non inoltra `aria-label`.
 *
 * Ruolo, focalizzabilità e gestione dei tasti vengono aggiunti solo se
 * l'elemento non è già un pulsante nativo, per non attivarlo due volte.
 */
export const useIconButtonA11y = (selector: string, label: string, expanded?: boolean): void => {
    useEffect(() => {
        const el = window.document.querySelector<HTMLElement>(selector);
        if (el === null) {
            return;
        }

        el.setAttribute("aria-label", label);
        expanded !== undefined && el.setAttribute("aria-expanded", expanded ? "true" : "false");

        if (el.tagName === "BUTTON") {
            return;
        }

        el.setAttribute("role", "button");
        el.setAttribute("tabindex", "0");

        const onKeyDown = (e: globalThis.KeyboardEvent) => {
            if (e.key !== "Enter" && e.key !== " " && e.key !== "Spacebar") {
                return;
            }
            e.preventDefault();
            el.click();
        };

        el.addEventListener("keydown", onKeyDown);
        return () => el.removeEventListener("keydown", onKeyDown);
    }, [selector, label, expanded]);
};
